import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, Building2, Car, Briefcase, TrendingUp, Package, Clock } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSET_TYPES = [
  { value: "property", label: "Property", icon: Building2 },
  { value: "vehicle", label: "Vehicle", icon: Car },
  { value: "business", label: "Business", icon: Briefcase },
  { value: "investment", label: "Investment", icon: TrendingUp },
  { value: "other", label: "Other", icon: Package },
];

function getTypeIcon(type) {
  return ASSET_TYPES.find(t => t.value === type)?.icon || Package;
}

function formatCurrency(val, currency = "NZD") {
  return new Intl.NumberFormat("en-NZ", { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);
}

function AssetCard({ asset, currency, onEdit, onDelete }) {
  const Icon = getTypeIcon(asset.asset_type);
  const netValue = (asset.current_value || 0) * (asset.ownership_pct || 100) / 100;

  return (
    <div className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:border-slate-300 dark:hover:border-slate-700 transition-colors" data-testid={`asset-card-${asset.asset_id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <Icon className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900 dark:text-slate-100">{asset.name}</h3>
            <p className="text-xs text-slate-500 capitalize">{asset.asset_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(asset)} data-testid={`edit-asset-${asset.asset_id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={() => onDelete(asset)} data-testid={`delete-asset-${asset.asset_id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-slate-400 mb-0.5">{asset.is_future ? "Estimated Cost" : "Value"}</p>
          <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {formatCurrency(asset.is_future ? asset.estimated_cost : asset.current_value, currency)}
          </p>
        </div>
        {!asset.is_future && asset.ownership_pct < 100 && (
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Your Share ({asset.ownership_pct}%)</p>
            <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">{formatCurrency(netValue, currency)}</p>
          </div>
        )}
        {asset.is_future && asset.target_date && (
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Target Date</p>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{new Date(asset.target_date).toLocaleDateString("en-NZ", { month: "short", year: "numeric" })}</p>
          </div>
        )}
      </div>

      {asset.notes && (
        <p className="text-xs text-slate-400 mt-3 line-clamp-2">{asset.notes}</p>
      )}
    </div>
  );
}

export function AssetsContent() {
  const { household } = useAuth();
  const [currentAssets, setCurrentAssets] = useState([]);
  const [futureAssets, setFutureAssets] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", asset_type: "other", current_value: "", ownership_pct: "100", notes: "", is_future: false, estimated_cost: "", target_date: "" });

  const currency = household?.currency || "NZD";

  const fetchData = useCallback(async () => {
    if (!household?.household_id) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/assets`, { credentials: "include" });
      if (!res.ok) {
        console.error(`[Assets] Fetch failed: ${res.status}`, await res.text().catch(() => ""));
        if (res.status === 401) toast.error("Session expired — please log in again.");
        else toast.error(`Could not load assets (${res.status}). Try refreshing.`);
        return;
      }
      const data = await res.json();
      setCurrentAssets(data.current_assets || []);
      setFutureAssets(data.future_assets || []);
      setSummary(data.summary || {});
    } catch (e) {
      console.error("[Assets] Network error:", e);
      toast.error("Could not reach the server. Check your connection and try again.");
    } finally { setLoading(false); }
  }, [household?.household_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = (isFuture = false) => {
    setEditing(null);
    setForm({ name: "", asset_type: "other", current_value: "", ownership_pct: "100", notes: "", is_future: isFuture, estimated_cost: "", target_date: "" });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name, asset_type: a.asset_type || "other", current_value: String(a.current_value || ""),
      ownership_pct: String(a.ownership_pct ?? 100), notes: a.notes || "",
      is_future: a.is_future || false, estimated_cost: String(a.estimated_cost || ""), target_date: a.target_date || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    const body = {
      name: form.name.trim(), asset_type: form.asset_type,
      current_value: parseFloat(form.current_value) || 0,
      ownership_pct: parseFloat(form.ownership_pct) || 100,
      notes: form.notes.trim() || null,
      is_future: form.is_future,
      estimated_cost: form.is_future ? (parseFloat(form.estimated_cost) || null) : null,
      target_date: form.target_date || null,
    };
    try {
      const url = editing
        ? `${API_URL}/api/households/${household.household_id}/assets/${editing.asset_id}`
        : `${API_URL}/api/households/${household.household_id}/assets`;
      const res = await fetch(url, { method: editing ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Save failed"); }
      toast.success(editing ? "Asset updated" : "Asset added");
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/api/households/${household.household_id}/assets/${a.asset_id}`, { method: "DELETE", credentials: "include" });
      toast.success("Asset removed");
      fetchData();
    } catch { toast.error("Could not delete asset."); }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-4xl mx-auto space-y-8" data-testid="assets-content">
      {/* Summary */}
      {(currentAssets.length > 0 || futureAssets.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Total Asset Value</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100 tabular-nums" data-testid="total-asset-value">{formatCurrency(summary.total_value, currency)}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Current Assets</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{summary.asset_count || 0}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Planned Purchases</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{summary.future_count || 0}</p>
          </div>
        </div>
      )}

      {/* Current Assets */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Current Assets</h2>
          <Button onClick={() => openAdd(false)} size="sm" variant="outline" className="rounded-full" data-testid="add-current-asset-btn">
            <Plus className="w-4 h-4 mr-1" />Add an asset
          </Button>
        </div>
        {currentAssets.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl" data-testid="current-assets-empty">
            <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium mb-1">Add your assets to see the bigger picture</p>
            <p className="text-xs text-muted-foreground mb-4">Property, vehicles, investments — track what you own and what you're growing.</p>
            <Button onClick={() => openAdd(false)} variant="outline" size="sm">Add an asset</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currentAssets.map(a => <AssetCard key={a.asset_id} asset={a} currency={currency} onEdit={openEdit} onDelete={handleDelete} />)}
          </div>
        )}
      </section>

      {/* Future / Planned Assets */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Planned Purchases</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Assets you're planning to buy in the future</p>
          </div>
          <Button onClick={() => openAdd(true)} size="sm" variant="outline" className="rounded-full" data-testid="add-future-asset-btn">
            <Plus className="w-4 h-4 mr-1" />Plan purchase
          </Button>
        </div>
        {futureAssets.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl" data-testid="future-assets-empty">
            <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium mb-1">Planning to buy something big?</p>
            <p className="text-xs text-muted-foreground mb-4">Add what you're working toward — a car, property, or anything meaningful.</p>
            <Button onClick={() => openAdd(true)} variant="outline" size="sm">Add a planned purchase</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {futureAssets.map(a => <AssetCard key={a.asset_id} asset={a} currency={currency} onEdit={openEdit} onDelete={handleDelete} />)}
          </div>
        )}
      </section>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby="asset-dialog-desc">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Asset" : form.is_future ? "Plan Future Purchase" : "Add Asset"}</DialogTitle>
          </DialogHeader>
          <p id="asset-dialog-desc" className="sr-only">{editing ? "Edit asset" : "Add asset"}</p>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Family Home" data-testid="asset-name-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Type</Label>
                <Select value={form.asset_type} onValueChange={v => setForm(p => ({ ...p, asset_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!form.is_future ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Current Value</Label>
                  <Input type="number" step="0.01" value={form.current_value} onChange={e => setForm(p => ({ ...p, current_value: e.target.value }))} placeholder="0" data-testid="asset-value-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Ownership %</Label>
                  <Input type="number" min="0" max="100" value={form.ownership_pct} onChange={e => setForm(p => ({ ...p, ownership_pct: e.target.value }))} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Estimated Cost</Label>
                  <Input type="number" step="0.01" value={form.estimated_cost} onChange={e => setForm(p => ({ ...p, estimated_cost: e.target.value }))} placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Target Date</Label>
                  <Input type="date" value={form.target_date} onChange={e => setForm(p => ({ ...p, target_date: e.target.value }))} />
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox" id="is-future"
                checked={form.is_future}
                onChange={e => setForm(p => ({ ...p, is_future: e.target.checked }))}
                className="rounded border-slate-300"
              />
              <Label htmlFor="is-future" className="text-xs text-slate-500 cursor-pointer">This is a future / planned purchase</Label>
            </div>

            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional details" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900" data-testid="save-asset-btn">
              {editing ? "Save Changes" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
